import { useCallback, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useAuthModal, useGuardedNavigate } from "../../context/AuthModalContext";
import { useTranslation } from "../../context/TranslationContext";
import BugReportModal from "../BugReportModal";
import Icon from "../ds/Icon";
import IconButton, { STATE_LAYER } from "../ds/IconButton";
import Menu, { MenuItem, MenuSeparator } from "../ds/Menu";
import ShellAvatar from "./ShellAvatar";
import { useDismiss } from "./useShellData";
import { getProfileAvatarUrl } from "../../utils/userDisplay";

/*
  Figma "Sidebar · პირადი" (handoff Sidebar): brand, My businesses selector,
  navigation, resources, Other businesses, and Menu at the bottom. 280 wide;
  `collapsed` is the 80px rail used beside full-height windows.
*/

const MAIN = [
  { id: "home", icon: "home", path: "/home", label: "navigation.home" },
  { id: "discover", icon: "compass", path: "/", label: "navigation.discover" },
  { id: "market", icon: "megaphone", path: "/affiliate-market", label: "navigation.affiliateMarketplace" },
  { id: "create", icon: "plus", path: "/create-business", label: "navigation.createBusiness" },
  { id: "invite", icon: "user-plus", path: "/referrals", label: "navigation.inviteFriends" },
];

const RESOURCES = [
  { id: "blog", icon: "book", path: "/resources", label: "sidebar.blog" },
  { id: "about", icon: "info", path: "/resources/about", label: "sidebar.about" },
];

const isActive = (item, pathname) => {
  if (item.id === "discover") return pathname === "/";
  if (item.id === "blog") return pathname === "/resources" || pathname.startsWith("/resources/news");
  return pathname === item.path || pathname.startsWith(`${item.path}/`);
};

/** The Mint Space the current page belongs to, if any. */
const currentSpaceId = (pathname) => pathname.match(/^\/mint-space\/([^/?#]+)/)?.[1] || null;

const GroupTitle = ({ children }) => (
  <h2 className="flex h-[18px] shrink-0 items-center text-caps-label uppercase text-label-secondary">{children}</h2>
);

const Spacer = () => <span className="block h-space-16 shrink-0" aria-hidden="true" />;

/* Figma "Sidebar item": 44 tall, 10/12 padding, radius/md, Caps/Menu label. */
const SidebarItem = ({ icon, label, selected = false, onClick, buttonRef, ...rest }) => (
  <button
    ref={buttonRef}
    type="button"
    aria-current={selected ? "page" : undefined}
    onClick={onClick}
    className={`relative flex min-h-[44px] w-full shrink-0 items-center gap-space-12 rounded-radius-md px-space-12 py-[10px] text-left outline-none focus-visible:shadow-[inset_0_0_0_2px_var(--color-focus-ring)] ${STATE_LAYER} ${
      selected ? "bg-control-selected text-control-selected-label" : "text-label-primary"
    }`}
    {...rest}
  >
    <Icon name={icon} size={20} />
    <span className="line-clamp-2 min-w-0 flex-1 text-caps-menu uppercase">{label}</span>
  </button>
);

/* Figma "Business option": Default (58) | Small (40) × State. */
const BusinessRow = ({ name, src, subtitle, size = "default", shape = "square", selected = false, onClick }) => (
  <button
    type="button"
    role="option"
    aria-selected={selected}
    onClick={onClick}
    className={`relative flex w-full items-center text-left outline-none focus-visible:shadow-[inset_0_0_0_2px_var(--color-focus-ring)] ${STATE_LAYER} ${
      size === "default" ? "gap-space-12 rounded-control p-space-8" : "min-h-[40px] gap-space-8 rounded-radius-sm p-space-4"
    } ${selected ? "bg-control-selected" : ""}`}
  >
    <ShellAvatar src={src} name={name} size={size === "default" ? "l" : "m"} shape={shape} />
    <span className="flex min-w-0 flex-1 flex-col gap-space-4">
      <span className={`truncate ${size === "default" ? "text-footnote text-label-primary" : "text-caption-1 text-label-secondary"}`}>
        {name}
      </span>
      {size === "default" && subtitle && <span className="text-caption-1 text-label-secondary">{subtitle}</span>}
    </span>
    {selected && <Icon name="check" size={24} className="text-label-primary" />}
  </button>
);

/* Figma "Business dropdown": the person's account, their businesses and Create business. */
const BusinessDropdown = ({ id, owned, selectedId, onPick, listRef, className = "", ...rest }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const heading = "p-space-8 text-caps-label uppercase text-label-tertiary";
  return (
    <div
      ref={listRef}
      id={id}
      role="listbox"
      aria-label={t("shell.myBusinesses")}
      className={`flex flex-col gap-space-4 overflow-y-auto rounded-radius-lg border border-separator-default bg-bg-raised p-space-8 shadow-overlay animate-ds-pop-in ${className}`}
      {...rest}
    >
      <p className={heading}>{t("shell.personalAccount")}</p>
      <BusinessRow
        name={user?.username || user?.businessName || ""}
        src={getProfileAvatarUrl(user)}
        subtitle={t("shell.personalAccount")}
        selected={!selectedId}
        onClick={() => onPick("/profile")}
      />
      {owned.length > 0 && (
        <>
          <p className={heading}>{t("shell.myBusinesses")}</p>
          {owned.map((space) => (
            <BusinessRow
              key={space._id}
              name={space.name}
              src={space.logo}
              subtitle={t("shell.owner")}
              selected={space._id === selectedId}
              onClick={() => onPick(`/mint-space/${space._id}`)}
            />
          ))}
        </>
      )}
      <hr className="my-space-4 h-px w-full shrink-0 border-0 bg-separator-default" />
      <button
        type="button"
        onClick={() => onPick("/create-business")}
        className={`relative flex min-h-[44px] items-center gap-space-12 rounded-radius-sm px-space-12 py-space-8 text-left ${STATE_LAYER}`}
      >
        <Icon name="plus" size={20} />
        <span className="text-subheadline text-accent-text">{t("navigation.createBusiness")}</span>
      </button>
    </div>
  );
};

/* Figma "Business selector": the account this page is in. */
const BusinessSelector = ({ owned, onGo }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const listId = useId();
  const close = useCallback(() => setOpen(false), []);
  useDismiss(ref, open, close);

  const spaceId = currentSpaceId(location.pathname);
  const current = owned.find((space) => space._id === spaceId) || null;
  const me = { name: user?.username || user?.businessName || "", src: getProfileAvatarUrl(user) };

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((o) => !o)}
        className={`relative flex w-full items-center gap-space-12 rounded-control text-left outline-none focus-visible:shadow-[0_0_0_2px_var(--color-focus-ring)] ${STATE_LAYER} ${
          open ? "border-[1.5px] border-accent-default p-[7.5px]" : "border border-separator-default p-space-8"
        }`}
      >
        {current ? (
          <ShellAvatar src={current.logo} name={current.name} size="l" shape="square" />
        ) : (
          <ShellAvatar src={me.src} name={me.name} size="l" shape="square" />
        )}
        <span className="flex min-w-0 flex-1 flex-col gap-space-4">
          <span className="truncate text-footnote text-label-primary">{current ? current.name : me.name}</span>
          <span className="text-caption-1 text-label-secondary">{current ? t("shell.owner") : t("shell.personalAccount")}</span>
        </span>
        <Icon name={open ? "chevron-up" : "chevron-down"} size={24} />
      </button>
      {open && (
        <BusinessDropdown
          id={listId}
          owned={owned}
          selectedId={current?._id}
          onPick={(path) => {
            close();
            onGo(path);
          }}
          className="absolute inset-x-0 top-[calc(100%+8px)] z-20 max-h-[60vh]"
        />
      )}
    </div>
  );
};

/*
  Figma's rail has an "apps" button after its two avatars. Figma links it to nothing, so it
  opens the Business dropdown beside the rail, for the businesses the rail has no room for.
*/
const RailBusinesses = ({ owned, selectedId, onGo, isDark }) => {
  const { t } = useTranslation();
  const [position, setPosition] = useState(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const refs = useMemo(() => [buttonRef, listRef], []);
  const close = useCallback(() => setPosition(null), []);
  useDismiss(refs, !!position, close);

  const toggle = () => {
    if (position) {
      close();
      return;
    }
    const { top } = buttonRef.current.getBoundingClientRect();
    setPosition({ top, maxHeight: window.innerHeight - top - 16 });
  };

  return (
    <div ref={buttonRef} className="shrink-0">
      <IconButton size="m" label={t("shell.myBusinesses")} aria-haspopup="listbox" aria-expanded={!!position} onClick={toggle}>
        <Icon name="apps" size={22} />
      </IconButton>
      {position &&
        createPortal(
          <div data-theme={isDark ? "dark" : "light"} className="font-ds text-label-primary">
            <BusinessDropdown
              owned={owned}
              selectedId={selectedId}
              onPick={(path) => {
                close();
                onGo(path);
              }}
              listRef={listRef}
              className="fixed left-[88px] z-[510] w-[248px]"
              style={position}
            />
          </div>,
          document.body
        )}
    </div>
  );
};

/* Figma "Other businesses": Search Closed | Open, sort and view all. */
const OtherBusinesses = ({ joined, onGo }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [searching, setSearching] = useState(false);
  const [query, setQuery] = useState("");
  const [sorted, setSorted] = useState(false);
  const [all, setAll] = useState(false);
  const spaceId = currentSpaceId(location.pathname);

  const list = useMemo(() => {
    let rows = joined.filter((space) => (space.name || "").toLowerCase().includes(query.trim().toLowerCase()));
    if (sorted) rows = [...rows].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    return all || query ? rows : rows.slice(0, 4);
  }, [joined, query, sorted, all]);

  const closeSearch = () => {
    setSearching(false);
    setQuery("");
  };

  return (
    <section aria-label={t("sidebar.otherBusinesses")} className="flex shrink-0 flex-col gap-space-4">
      <GroupTitle>{t("sidebar.otherBusinesses")}</GroupTitle>
      <div className="flex items-center justify-end gap-space-4 pb-space-4">
        {searching ? (
          <label className="flex h-8 min-w-0 flex-1 items-center gap-space-8 rounded-full bg-fill-tertiary pl-[10px] pr-[6px] text-label-tertiary shadow-edge">
            <Icon name="search" size={16} />
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => event.key === "Escape" && closeSearch()}
              placeholder={t("shell.searchBusinesses")}
              aria-label={t("shell.searchBusinesses")}
              className="min-w-0 flex-1 bg-transparent text-footnote text-label-primary outline-none placeholder:text-label-tertiary"
            />
            <button
              type="button"
              onClick={closeSearch}
              aria-label={t("common.cancel")}
              className="grid size-6 place-items-center rounded-full text-label-tertiary hover:text-label-primary"
            >
              <Icon name="close" size={16} />
            </button>
          </label>
        ) : (
          <IconButton label={t("common.search")} onClick={() => setSearching(true)}>
            <Icon name="search" size={18} />
          </IconButton>
        )}
        <IconButton label={t("shell.sortAZ")} aria-pressed={sorted} onClick={() => setSorted((s) => !s)}>
          <Icon name="filter" size={18} />
        </IconButton>
        <IconButton label={t("shell.viewAll")} aria-pressed={all} onClick={() => setAll((a) => !a)}>
          <Icon name="chevrons-right" size={18} />
        </IconButton>
      </div>
      <div role="listbox" aria-label={t("sidebar.otherBusinesses")} className="flex flex-col">
        {list.length > 0 ? (
          list.map((space) => (
            <BusinessRow
              key={space._id}
              name={space.name}
              src={space.logo}
              size="small"
              selected={space._id === spaceId}
              onClick={() => onGo(`/mint-space/${space._id}`)}
            />
          ))
        ) : (
          <p className="p-space-8 text-footnote text-label-tertiary">{t("shell.noResults")}</p>
        )}
      </div>
    </section>
  );
};

/* Figma E7, the Menu: account pages and support, the language, the app's own settings, signing out. */
const SettingsMenu = ({ anchorRef, open, onClose, onGo, isDark, toggleTheme, setToast, onBugReport }) => {
  const { isAuthenticated, logout } = useAuth();
  const { openAuthModal } = useAuthModal();
  const { t, language, changeLanguage } = useTranslation();

  return (
    <Menu open={open} onClose={onClose} anchorRef={anchorRef} width={280} align="start" label={t("navigation.menu")}>
      {isAuthenticated && (
        <MenuItem icon="wallet" onClick={() => onGo("/purchases")}>
          {t("navigation.purchases")}
        </MenuItem>
      )}
      <MenuItem icon="message" onClick={() => onGo("/support")}>
        {t("navigation.support")}
      </MenuItem>
      <MenuItem icon="info" onClick={onBugReport}>
        {t("navigation.reportBug")}
      </MenuItem>
      <MenuSeparator />
      <p className="px-space-12 py-space-8 text-caps-label uppercase text-label-tertiary">{t("navigation.language")}</p>
      {[
        ["en", "English"],
        ["ka", "ქართული"],
      ].map(([code, name]) => (
        <MenuItem
          key={code}
          selected={language === code}
          detail={language === code ? <Icon name="check" size={20} className="shrink-0 text-accent-text" /> : null}
          onClick={() => changeLanguage(code)}
        >
          {name}
        </MenuItem>
      ))}
      <MenuSeparator />
      <MenuItem icon="sparkle" onClick={toggleTheme}>
        {isDark ? t("shell.lightTheme") : t("shell.darkTheme")}
      </MenuItem>
      <MenuItem icon="book" onClick={() => setToast?.(t("toasts.legalComingSoon"))}>
        {t("campaigns.legal")}
      </MenuItem>
      <MenuSeparator />
      {isAuthenticated ? (
        <MenuItem
          icon="logout"
          danger
          onClick={() => {
            logout();
            onGo("/auth");
            setToast?.(t("common.loggedOut"));
          }}
        >
          {t("auth.logOut")}
        </MenuItem>
      ) : (
        <MenuItem icon="user-plus" onClick={() => openAuthModal({ form: "signin" })}>
          {t("auth.signIn")}
        </MenuItem>
      )}
    </Menu>
  );
};

const ShellSidebar = ({ owned, joined, collapsed = false, isDark, toggleTheme, setToast, onNavigated }) => {
  const { isAuthenticated, user } = useAuth();
  const { t } = useTranslation();
  const navigate = useGuardedNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [bugReport, setBugReport] = useState(false);
  const menuButtonRef = useRef(null);
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  const go = (path) => {
    navigate(path);
    onNavigated?.();
  };

  const menu = (
    <>
      <SettingsMenu
        anchorRef={menuButtonRef}
        open={menuOpen}
        onClose={closeMenu}
        onGo={go}
        isDark={isDark}
        toggleTheme={toggleTheme}
        setToast={setToast}
        onBugReport={() => setBugReport(true)}
      />
      <BugReportModal isOpen={bugReport} onClose={() => setBugReport(false)} isDark={isDark} setToast={setToast} />
    </>
  );

  if (collapsed) {
    const spaceId = currentSpaceId(location.pathname);
    // Two avatars, as in Figma: the account, then the business in view or the first one.
    const railSpace = owned.find((space) => space._id === spaceId) || owned[0];
    const railAvatar = (key, name, src, shape, path, selected) => (
      <button
        key={key}
        type="button"
        title={name}
        aria-label={name}
        aria-current={selected ? "page" : undefined}
        onClick={() => go(path)}
        className={`relative shrink-0 rounded-full outline-none focus-visible:shadow-[0_0_0_2px_var(--color-focus-ring)] ${STATE_LAYER} ${
          selected ? "ring-2 ring-accent-default" : ""
        }`}
      >
        <ShellAvatar src={src} name={name} shape={shape} />
      </button>
    );
    return (
      <nav
        aria-label={t("navigation.navigation")}
        className="flex h-full w-[80px] shrink-0 flex-col items-center gap-space-8 overflow-y-auto overflow-x-hidden border-r border-separator-default bg-bg-sidebar px-[18px] pb-space-16 pt-space-20 font-ds text-label-primary"
      >
        <button type="button" onClick={() => go("/")} aria-label="Mintly" className="size-7 shrink-0 rounded-radius-sm bg-accent-default" />
        <hr className="my-space-4 h-px w-full shrink-0 border-0 bg-separator-default" />
        {isAuthenticated && (
          <>
            {railAvatar("me", user?.username || "", getProfileAvatarUrl(user), "round", "/profile", location.pathname.startsWith("/profile"))}
            {railSpace &&
              railAvatar(railSpace._id, railSpace.name, railSpace.logo, "square", `/mint-space/${railSpace._id}`, railSpace._id === spaceId)}
            {owned.length > 1 && <RailBusinesses owned={owned} selectedId={spaceId} onGo={go} isDark={isDark} />}
            <hr className="my-space-4 h-px w-full shrink-0 border-0 bg-separator-default" />
          </>
        )}
        {[...MAIN, ...RESOURCES].map((entry) => {
          const selected = isActive(entry, location.pathname);
          return (
            <IconButton
              key={entry.id}
              size="m"
              variant={selected ? "gray" : "plain"}
              selected={selected}
              label={t(entry.label)}
              aria-current={selected ? "page" : undefined}
              onClick={() => go(entry.path)}
            >
              <Icon name={entry.icon} size={22} />
            </IconButton>
          );
        })}
        {joined.length > 0 && (
          <>
            <hr className="my-space-4 h-px w-full shrink-0 border-0 bg-separator-default" />
            {joined
              .slice(0, 4)
              .map((space) => railAvatar(space._id, space.name, space.logo, "square", `/mint-space/${space._id}`, space._id === spaceId))}
          </>
        )}
        <span className="flex-1" />
        <span ref={menuButtonRef}>
          <IconButton size="m" variant="plain" label={t("navigation.menu")} aria-expanded={menuOpen} onClick={() => setMenuOpen((o) => !o)}>
            <Icon name="settings" size={22} />
          </IconButton>
        </span>
        {menu}
      </nav>
    );
  }

  return (
    <nav
      aria-label={t("navigation.navigation")}
      className="flex h-full w-[280px] shrink-0 flex-col gap-[2px] overflow-y-auto overflow-x-hidden border-r border-separator-default bg-bg-sidebar px-space-16 pb-space-16 pt-space-20 font-ds text-label-primary [scrollbar-width:thin]"
    >
      <button type="button" onClick={() => go("/")} className="flex h-[44px] shrink-0 items-center gap-[10px] pb-space-16 pl-space-12 text-left">
        <span className="size-7 shrink-0 rounded-radius-sm bg-accent-default" aria-hidden="true" />
        <span className="text-headline">Mintly</span>
      </button>

      {isAuthenticated && (
        <>
          <GroupTitle>{t("shell.myBusinesses")}</GroupTitle>
          <BusinessSelector owned={owned} onGo={go} />
          <Spacer />
        </>
      )}

      <GroupTitle>{t("navigation.navigation")}</GroupTitle>
      {MAIN.map((entry) => (
        <SidebarItem
          key={entry.id}
          icon={entry.icon}
          label={t(entry.label)}
          selected={isActive(entry, location.pathname)}
          onClick={() => go(entry.path)}
        />
      ))}
      <Spacer />

      <GroupTitle>{t("sidebar.resources")}</GroupTitle>
      {RESOURCES.map((entry) => (
        <SidebarItem
          key={entry.id}
          icon={entry.icon}
          label={t(entry.label)}
          selected={isActive(entry, location.pathname)}
          onClick={() => go(entry.path)}
        />
      ))}

      {isAuthenticated && joined.length > 0 && (
        <>
          <Spacer />
          <OtherBusinesses joined={joined} onGo={go} />
        </>
      )}

      <span className="flex-1" />
      <SidebarItem
        icon="settings"
        label={t("navigation.menu")}
        buttonRef={menuButtonRef}
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((o) => !o)}
      />
      {menu}
    </nav>
  );
};

export default ShellSidebar;
